/* eslint-disable */
/**
 * HjemmesidePanel.jsx — Hjemmeside-panel i nyt OS-dashboard (Sager-stil)
 *
 * Erstatter den gamle WebsiteStudio-komponent og dens 18-fane-sidebar.
 * Genbrug af DATA-endpoints fra os-website-studio.js:
 *   GET  /api/os/intern/studio/sider           → liste af Astro-sider i repoet
 *   GET  /api/os/intern/studio/sider/:navn      → kildekode + SHA for en side
 *   PUT  /api/os/intern/studio/sider/:navn      → gem kildekode (kræver René-godkendelse via beslutning)
 *   POST /api/os/intern/studio/ai               → AI-redigering af side
 *   GET  /api/os/intern/studio/deploy-status    → seneste GitHub Actions-kørsel
 *
 * Trafikdata (GA4/SC/LLM): hentes fra /api/os/website-analytics/* — viser real data
 * eller korrekt fejltilstand hvis env vars mangler (GA4_PROPERTY_ID, SEARCH_CONSOLE_URL).
 *
 * Regler:
 *   - Ingen fake/hardkodede tal
 *   - TomTilstand ved fejl eller ikke-forbundet endpoint
 *   - Korrekt dansk (æøå) i al UI-tekst
 *   - Sager-stil: KpiKort-række, DataTabel, SektionsHeader, Boks
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  KpiKort,
  StatusBadge,
  TomTilstand,
  DataTabel,
  SektionsHeader,
  AfventerBadge,
  useApiPolling,
  STATUS,
  TEKST,
  BG,
  KANT,
  RADIUS,
} from '../komponenter/index';
import { Boks } from '../ui/Boks';

// ─── API-hjælper ──────────────────────────────────────────────────────────────
async function api(sti, metode = 'GET', krop = null) {
  const opts = {
    method: metode,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  };
  if (krop) opts.body = JSON.stringify(krop);
  const r = await fetch('/api' + sti, opts);
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json();
}


// ─── Deploy-statusbadge ───────────────────────────────────────────────────────
function DeployStatusBadge({ status }) {
  if (!status || status === 'ukendt') {
    return <StatusBadge status="IKKE FORBUNDET" tekst="ukendt" lille />;
  }
  if (status === 'success' || status === 'completed') {
    return <StatusBadge status="LIVE" tekst="succes" lille />;
  }
  if (status === 'in_progress' || status === 'queued') {
    return <StatusBadge status="OPMÆRKSOMHED" tekst={status} lille puls />;
  }
  if (status === 'failure') {
    return <StatusBadge status="FEJL" tekst="fejlet" lille />;
  }
  return <StatusBadge status="KONFIGURERET" tekst={status} lille />;
}

// ─── Sidenavne (kortform) ─────────────────────────────────────────────────────
// Accepterer URL-sti (/viden/guide-bisidder/) eller filsti (src/pages/index.astro)
function sideNavn(sti) {
  if (!sti) return '—';
  // URL-sti: fjern trailing slash, tag sidste segment
  const rens = sti.replace(/\/$/, '');
  const segment = rens.split('/').filter(Boolean).pop() || 'index';
  const fil = segment.replace('.astro', '').replace('.md', '');

  const navne = {
    index:          'Forside',
    'om-os':        'Om os',
    'om-skilsmissevejen': 'Om SkilsmisseVejen',
    priser:         'Priser',
    kontakt:        'Kontakt',
    blog:           'Blog',
    faq:            'FAQ',
    viden:          'Viden (oversigt)',
    videoer:        'Videoer (oversigt)',
    ydelser:        'Ydelser (oversigt)',
    beregner:       'Beregner (oversigt)',
    'd':            'Redirect /d',
    'r':            'Redirect /r',
    'hele-danmark': 'Hele Danmark',
    'forretningsbetingelser': 'Forretningsbetingelser',
    'persondatapolitik': 'Persondatapolitik',
    familieretshuset: 'Familieretshuset (oversigt)',
    'skilsmisse-guide': 'Skilsmisse-guide',
    'foraeldre-myndighed': 'Forældremyndighed',
    samvaer:        'Samvær',
    bodeling:       'Bodeling',
    'bornebidrag':  'Børnebidrag',
    'gratis-guide': 'Gratis guide',
  };
  if (navne[fil]) return navne[fil];

  // Formater automatisk: streg→mellemrum, første bogstav stort
  return fil.charAt(0).toUpperCase() + fil.slice(1).replace(/-/g, ' ');
}

// ─── AI-editor modal ─────────────────────────────────────────────────────────
function AIEditorModal({ side, onLuk }) {
  const [indhold, setIndhold] = useState('');
  const [sha, setSha] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [status, setStatus] = useState(''); // '', 'henter', 'genererer', 'pusher', 'deploy', 'faerdig', 'fejl'
  const [fejlTekst, setFejlTekst] = useState('');
  const [kildekodeAaben, setKildekodeAaben] = useState(false);

  useEffect(() => {
    if (!side) return;
    setStatus('henter');
    setIndhold('');
    setSha(null);
    api('/os/intern/studio/sider/' + encodeURIComponent(side))
      .then(d => {
        setIndhold(d.indhold || '');
        setSha(d.sha || null);
        setStatus('');
      })
      .catch(() => setStatus('fejl'));
  }, [side]);

  const genererMedAI = async () => {
    if (!prompt.trim() || !indhold) return;
    setStatus('genererer');
    setFejlTekst('');
    try {
      const res = await api('/os/intern/studio/ai', 'POST', {
        side,
        prompt,
        nuvaerende_indhold: indhold,
      });
      if (res.fejl || typeof res.nyt_indhold !== 'string' || !res.nyt_indhold.trim()) {
        setStatus('fejl');
        setFejlTekst(res.fejl || 'AI returnerede tomt svar');
        return;
      }
      setIndhold(res.nyt_indhold);
      setStatus('faerdig');
      setTimeout(() => setStatus(''), 4000);
    } catch (e) {
      setStatus('fejl');
      setFejlTekst(e.message);
    }
  };

  const gemManuelt = async () => {
    if (!indhold) return;
    setStatus('pusher');
    try {
      await api('/os/intern/studio/sider/' + encodeURIComponent(side), 'PUT', { indhold, sha });
      setStatus('faerdig');
      setTimeout(() => setStatus(''), 3000);
    } catch (e) {
      setStatus('fejl');
      setFejlTekst(e.message);
    }
  };

  const statusInfo = {
    henter:     { tekst: 'Henter kildekode…',           farve: STATUS.konfigureret },
    genererer:  { tekst: 'Genererer med AI…',            farve: STATUS.ai },
    pusher:     { tekst: 'Pusher til GitHub…',           farve: STATUS.konfigureret },
    faerdig:    { tekst: 'Færdig!',                      farve: STATUS.live },
    fejl:       { tekst: fejlTekst || 'Noget gik galt.', farve: STATUS.fejl },
  }[status];

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onLuk(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 900,
        background: 'rgba(0,0,0,0.60)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{
        width: '100%', maxWidth: 600, maxHeight: '90vh',
        background: 'rgba(7,12,26,0.98)',
        border: `1px solid ${KANT.aktiv}`,
        borderRadius: RADIUS.kort + 2,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Modal-header */}
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${KANT.standard}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <span style={{ fontWeight: 700, fontSize: 15, color: TEKST.primær }}>
              AI-rediger side
            </span>
            <span style={{ marginLeft: 10, fontSize: 12, color: TEKST.sekundær }}>
              {sideNavn(side)}
            </span>
          </div>
          <button
            onClick={onLuk}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: TEKST.sekundær, fontSize: 20, lineHeight: 1, padding: '0 4px',
            }}
          >×</button>
        </div>

        {/* Modal-krop */}
        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>

          {/* Status-besked */}
          {statusInfo && (
            <div style={{
              marginBottom: 14, padding: '8px 12px', borderRadius: RADIUS.badge,
              background: statusInfo.farve + '18', color: statusInfo.farve,
              fontSize: 13, fontWeight: 600, border: `1px solid ${statusInfo.farve}30`,
            }}>
              {statusInfo.tekst}
            </div>
          )}

          {/* Prompt-felt */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: TEKST.sekundær, marginBottom: 6, fontWeight: 600 }}>
              Hvad skal ændres?
            </div>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              onKeyDown={e => { if (e.ctrlKey && e.key === 'Enter') genererMedAI(); }}
              placeholder={"Eksempler:\n• Gør hero-teksten mere overbevisende\n• Tilføj en ny testimonial\n• Opdater prisen til 4.495 kr"}
              rows={5}
              disabled={!!status && status !== 'fejl' && status !== 'faerdig'}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 12px', borderRadius: RADIUS.knap, fontSize: 13,
                border: `1px solid ${KANT.standard}`, resize: 'vertical',
                fontFamily: 'inherit',
                background: 'rgba(255,255,255,0.05)', color: TEKST.primær,
                outline: 'none', lineHeight: 1.5,
              }}
            />
          </div>

          {/* Generer-knap */}
          <button
            onClick={genererMedAI}
            disabled={!prompt.trim() || !indhold || (!!status && status !== 'fejl' && status !== 'faerdig')}
            style={{
              width: '100%', padding: '10px 0',
              background: (!prompt.trim() || !indhold || (!!status && status !== 'fejl' && status !== 'faerdig'))
                ? 'rgba(255,255,255,0.08)' : STATUS.ai,
              color: (!prompt.trim() || !indhold || (!!status && status !== 'fejl' && status !== 'faerdig'))
                ? TEKST.sekundær : '#fff',
              border: 'none', borderRadius: RADIUS.knap,
              fontSize: 14, fontWeight: 700, cursor: 'pointer',
              marginBottom: 16,
              transition: 'background .15s',
            }}
          >
            Generer med AI
            <span style={{ fontSize: 11, fontWeight: 400, marginLeft: 6, opacity: 0.75 }}>Ctrl+Enter</span>
          </button>

          {/* Kildekode (sammenfoldelig) */}
          <div style={{ borderTop: `1px solid ${KANT.standard}`, paddingTop: 14 }}>
            <button
              onClick={() => setKildekodeAaben(v => !v)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: TEKST.sekundær, fontSize: 13, fontWeight: 600,
                padding: 0, display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <span style={{
                display: 'inline-block',
                transform: kildekodeAaben ? 'rotate(90deg)' : 'none',
                transition: 'transform .15s',
              }}>▶</span>
              Kildekode (Astro)
            </button>
            {kildekodeAaben && (
              <div style={{ marginTop: 12 }}>
                <textarea
                  value={indhold}
                  onChange={e => setIndhold(e.target.value)}
                  rows={16}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '10px 12px', borderRadius: RADIUS.knap, fontSize: 11,
                    border: `1px solid ${KANT.standard}`,
                    fontFamily: '"SF Mono","Fira Code","Consolas",monospace',
                    background: 'rgba(6,12,28,0.90)', color: '#c8d8ee',
                    outline: 'none', lineHeight: 1.5, resize: 'vertical',
                  }}
                />
                <button
                  onClick={gemManuelt}
                  disabled={!indhold || (!!status && status !== 'fejl' && status !== 'faerdig')}
                  style={{
                    marginTop: 8, padding: '7px 18px',
                    background: 'rgba(59,130,246,0.15)',
                    color: STATUS.konfigureret,
                    border: `1px solid rgba(59,130,246,0.30)`,
                    borderRadius: RADIUS.knap,
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Gem kildekode manuelt
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── PANEL ────────────────────────────────────────────────────────────────────
export default function HjemmesidePanel() {

  // ── Deploy-status (poller hvert 30. sekund) ───────────────────────────────
  const {
    data:      deployData,
    loading:   deployLoading,
    opdateret: deployOpdateret,
    refresh:   deployRefresh,
  } = useApiPolling('/os/intern/studio/deploy-status', 90000);

  // ── Sideliste ─────────────────────────────────────────────────────────────
  const [sider, setSider]           = useState([]);
  const [siderLoading, setSiderLoading] = useState(true);
  const [siderFejl, setSiderFejl]   = useState(null);

  const hentSider = useCallback(() => {
    setSiderLoading(true);
    setSiderFejl(null);
    api('/os/intern/studio/sider')
      .then(d => {
        // Brug sider_fuld (sitemap-baseret) hvis tilgængeligt, ellers fallback til sider-array
        if (Array.isArray(d.sider_fuld) && d.sider_fuld.length > 0) {
          setSider(d.sider_fuld);
        } else if (Array.isArray(d.sider)) {
          // Bagudkompatibelt format: list af kildefil-stier
          setSider(d.sider.map(sti => ({ url: null, path: null, lastmod: null, kildefil: sti })));
        } else {
          setSider([]);
        }
        setSiderLoading(false);
      })
      .catch(e => {
        setSiderFejl(e.message);
        setSiderLoading(false);
      });
  }, []);

  useEffect(() => { hentSider(); }, [hentSider]);

  // ── AI-editor modal ────────────────────────────────────────────────────────
  const [valgtSide, setValgtSide] = useState(null);

  // ── Analytics-data ─────────────────────────────────────────────────────────
  const {
    data:    ga4Data,
    loading: ga4Loading,
    refresh: ga4Refresh,
  } = useApiPolling('/os/website-analytics/ga4?periode=30d', 300000);

  const {
    data:    scData,
    loading: scLoading,
    refresh: scRefresh,
  } = useApiPolling('/os/website-analytics/search-console?periode=30d', 300000);

  const {
    data:    llmData,
    loading: llmLoading,
    refresh: llmRefresh,
  } = useApiPolling('/os/website-analytics/llm-visibility', 3600000);

  // ── Refresh alt ───────────────────────────────────────────────────────────
  const [refreshing, setRefreshing] = useState(false);
  const refreshAlt = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([deployRefresh(), hentSider(), ga4Refresh(), scRefresh(), llmRefresh()]);
    setRefreshing(false);
  }, [deployRefresh, hentSider, ga4Refresh, scRefresh, llmRefresh]);

  // ── Udled deploy-data ─────────────────────────────────────────────────────
  const deployStatus  = deployData?.status  || 'ukendt';
  const deployNavn    = deployData?.navn     || null;
  const deployStartet = deployData?.startet  || null;
  const deployUrl     = deployData?.url      || null;

  // ── Sider-kolonner til DataTabel ──────────────────────────────────────────
  // sider er nu array af { url, path, lastmod, kildefil }
  const siderKolonner = [
    {
      key:   'visNavn',
      label: 'Side',
      render: (_, r) => (
        <span style={{ fontWeight: 600, color: TEKST.primær }}>{r.visNavn}</span>
      ),
    },
    {
      key:   'path',
      label: 'URL-sti',
      render: (v, r) => {
        const sti = v || (r.kildefil ? '/' + r.kildefil.replace('src/pages/', '').replace('.astro', '').replace(/\/index$/, '') : '—');
        return (
          <span style={{ fontSize: 12, color: TEKST.sekundær, fontFamily: '"SF Mono","Fira Code",monospace' }}>
            {sti === '/' ? '/' : sti}
          </span>
        );
      },
    },
    {
      key:   'url',
      label: 'Live',
      render: (v, r) => {
        const href = v || (r.kildefil ? 'https://www.skilsmissevejen.dk/' + r.kildefil.replace('src/pages/', '').replace('.astro', '').replace(/\/index$/, '') : null);
        if (!href) return <span style={{ color: TEKST.dæmpet, fontSize: 12 }}>—</span>;
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: TEKST.link, fontSize: 12, textDecoration: 'none' }}
            onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
            onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
          >
            Åbn ↗
          </a>
        );
      },
    },
    {
      key:   'rediger',
      label: '',
      align: 'right',
      render: (_, r) => (
        <button
          onClick={() => setValgtSide(r.kildefil)}
          disabled={!r.kildefil}
          style={{
            padding: '4px 12px',
            background: r.kildefil ? 'rgba(139,92,246,0.12)' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${r.kildefil ? 'rgba(139,92,246,0.28)' : 'rgba(255,255,255,0.08)'}`,
            borderRadius: RADIUS.knap,
            color: r.kildefil ? STATUS.ai : TEKST.dæmpet,
            fontSize: 12, fontWeight: 600,
            cursor: r.kildefil ? 'pointer' : 'default',
          }}
        >
          AI-rediger
        </button>
      ),
    },
  ];

  // Transformer sider-array til rækker
  // sider er nu array af { url, path, lastmod, kildefil }
  const siderRækker = sider.map(s => ({
    ...s,
    visNavn: s.path ? sideNavn(s.path) : (s.kildefil ? sideNavn(s.kildefil) : '—'),
  }));

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      {/* Panel-header */}
      <SektionsHeader
        label="Hjemmeside"
        opdateret={deployOpdateret}
        onRefresh={refreshAlt}
        refreshing={refreshing}
        højreIndhold={
          deployStatus === 'failure'
            ? <AfventerBadge antal={1} label="deploy fejlet" />
            : null
        }
        style={{ marginBottom: 20 }}
      />

      {/* ── KPI-række ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 22 }}>

        <KpiKort
          label="Live sider"
          value={siderLoading ? null : sider.length || 0}
          farve={siderFejl ? STATUS.ikkeForbundet : STATUS.live}
          ikon="◻"
          badge={siderFejl ? 'IKKE FORBUNDET' : 'SITEMAP'}
          loading={siderLoading}
        />

        <KpiKort
          label="Seneste deploy"
          value={deployLoading ? null : (
            deployStatus === 'ukendt' ? '—' :
            deployStatus === 'success' || deployStatus === 'completed' ? 'Succes' :
            deployStatus === 'failure' ? 'Fejlet' :
            deployStatus === 'in_progress' ? 'Kører' :
            deployStatus
          )}
          farve={
            deployStatus === 'success' || deployStatus === 'completed' ? STATUS.live :
            deployStatus === 'failure' ? STATUS.fejl :
            deployStatus === 'in_progress' ? STATUS.opmærksomhed :
            STATUS.ikkeForbundet
          }
          ikon="↑"
          badge={
            deployStatus === 'ukendt' ? 'IKKE FORBUNDET' :
            deployStatus === 'success' || deployStatus === 'completed' ? 'LIVE' :
            deployStatus === 'failure' ? 'FEJL' : 'OPMÆRKSOMHED'
          }
          loading={deployLoading}
        />

        {/* Trafik — GA4-sessioner */}
        <KpiKort
          label="Månedlig trafik"
          value={
            ga4Loading ? '…' :
            ga4Data?.mangler ? '—' :
            (ga4Data?.data?.sessions ?? ga4Data?.data?.totalSessions ?? ga4Data?.data?.screenPageViews ?? '—')
          }
          farve={
            ga4Loading    ? STATUS.ikkeForbundet :
            ga4Data?.mangler ? STATUS.opmærksomhed :
            ga4Data?.data    ? STATUS.live :
            STATUS.ikkeForbundet
          }
          ikon="◈"
          badge={
            ga4Loading       ? 'HENTER' :
            ga4Data?.mangler ? 'KONFIGURATION MANGLER' :
            ga4Data?.data    ? 'GA4 · 30 DAGE' :
            'FEJL'
          }
          loading={ga4Loading}
        />

      </div>

      {/* ── To-kolonne: Deploy-status + Hjemmeside-link ───────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18, marginBottom: 22 }}>

        {/* Deploy-status */}
        <Boks>
          <SektionsHeader label="Deploy-status" style={{ marginBottom: 14 }} />
          {deployLoading ? (
            <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter…</div>
          ) : deployStatus === 'ukendt' ? (
            <TomTilstand
              titel="Deploy-status ukendt"
              besked="Hentes fra GitHub Actions via /api/os/intern/studio/deploy-status"
              ikon="↑"
              badge="IKKE FORBUNDET"
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <DeployStatusBadge status={deployStatus} />
                {deployNavn && (
                  <span style={{ fontSize: 13, color: TEKST.primær, fontWeight: 600 }}>
                    {deployNavn}
                  </span>
                )}
              </div>
              {deployStartet && (
                <div style={{ fontSize: 12, color: TEKST.sekundær }}>
                  Startet:{' '}
                  {new Date(deployStartet).toLocaleString('da-DK', {
                    day: 'numeric', month: 'short',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </div>
              )}
              {deployUrl && (
                <a
                  href={deployUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: 12, color: TEKST.link,
                    textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                  onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                >
                  Se GitHub Actions ↗
                </a>
              )}
              <button
                onClick={() => deployRefresh()}
                style={{
                  alignSelf: 'flex-start',
                  padding: '5px 12px',
                  background: 'rgba(59,130,246,0.10)',
                  border: `1px solid rgba(59,130,246,0.25)`,
                  borderRadius: RADIUS.knap,
                  color: STATUS.konfigureret,
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
              >
                ↻ Opdatér status
              </button>
            </div>
          )}
        </Boks>

        {/* Hjemmeside-link + info */}
        <Boks>
          <SektionsHeader label="Hjemmeside" style={{ marginBottom: 14 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ margin: 0, fontSize: 13, color: TEKST.sekundær, lineHeight: 1.6 }}>
              Hjemmesiden er bygget med Astro og hostet via Cloudflare Pages.
              Ændringer sker via AI-rediger eller manuel kildekode-redigering herunder.
            </p>
            <a
              href="https://www.skilsmissevejen.dk"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 16px',
                background: '#f97316',
                color: '#fff', borderRadius: RADIUS.knap,
                fontSize: 13, fontWeight: 700, textDecoration: 'none',
                alignSelf: 'flex-start',
              }}
            >
              Åbn hjemmeside ↗
            </a>
            <div style={{ fontSize: 11, color: TEKST.dæmpet }}>
              Hjemmesiden blokerer indlejring (X-Frame-Options). Åbner i ny fane.
            </div>
          </div>
        </Boks>

      </div>

      {/* ── Trafik og konvertering ─────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18, marginBottom: 22 }}>

        {/* GA4 */}
        <Boks>
          <SektionsHeader label="Google Analytics 4" style={{ marginBottom: 12 }} />
          {ga4Loading ? (
            <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter GA4-data…</div>
          ) : ga4Data?.mangler ? (
            <TomTilstand
              titel="GA4 ikke konfigureret"
              besked={`Mangler miljøvariabel: ${ga4Data.mangler}. Tilføj GA4_PROPERTY_ID til server .env (find den i Google Analytics → Admin → Property Settings → Property ID).`}
              ikon="◈"
              badge="KONFIGURATION MANGLER"
            />
          ) : ga4Data?.data ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {Object.entries(ga4Data.data)
                .filter(([, v]) => v !== null && typeof v !== 'object')
                .slice(0, 8)
                .map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: TEKST.sekundær }}>{k}</span>
                    <span style={{ color: TEKST.primær, fontWeight: 600 }}>{String(v)}</span>
                  </div>
                ))}
              {ga4Data.fra_cache && (
                <div style={{ fontSize: 11, color: TEKST.dæmpet, marginTop: 4 }}>Fra cache</div>
              )}
            </div>
          ) : (
            <TomTilstand
              titel="GA4 data utilgængeligt"
              besked="Backend returnerede ingen data. Tjek at Google service account har adgang til GA4 property."
              ikon="◈"
              badge="FEJL"
            />
          )}
        </Boks>

        {/* Search Console */}
        <Boks>
          <SektionsHeader label="Search Console" style={{ marginBottom: 12 }} />
          {scLoading ? (
            <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter Search Console-data…</div>
          ) : scData?.mangler ? (
            <TomTilstand
              titel="Search Console ikke konfigureret"
              besked={`Mangler miljøvariabel: ${scData.mangler}. Tilføj SEARCH_CONSOLE_URL=https://www.skilsmissevejen.dk/ til server .env.`}
              ikon="◈"
              badge="KONFIGURATION MANGLER"
            />
          ) : scData?.data ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {Object.entries(scData.data)
                .filter(([, v]) => v !== null && typeof v !== 'object')
                .slice(0, 8)
                .map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: TEKST.sekundær }}>{k}</span>
                    <span style={{ color: TEKST.primær, fontWeight: 600 }}>{String(v)}</span>
                  </div>
                ))}
              {scData.fra_cache && (
                <div style={{ fontSize: 11, color: TEKST.dæmpet, marginTop: 4 }}>Fra cache</div>
              )}
            </div>
          ) : (
            <TomTilstand
              titel="Search Console data utilgængeligt"
              besked="Backend returnerede ingen data."
              ikon="◈"
              badge="FEJL"
            />
          )}
        </Boks>

        {/* LLM-synlighed */}
        <Boks>
          <SektionsHeader label="LLM-synlighed" style={{ marginBottom: 12 }} />
          {llmLoading ? (
            <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter LLM-data…</div>
          ) : llmData?.resultater?.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {llmData.resultater.map((r, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontSize: 12, padding: '4px 0', borderBottom: i < llmData.resultater.length - 1 ? `1px solid ${KANT.standard}` : 'none',
                }}>
                  <span style={{ color: TEKST.sekundær }}>{r.llm_navn} · {r.prompt_type}</span>
                  <span style={{
                    color: r.naevner_sv ? STATUS.live : STATUS.ikkeForbundet,
                    fontWeight: 700, fontSize: 13,
                  }}>
                    {r.naevner_sv ? '✓' : '✗'}
                  </span>
                </div>
              ))}
              {llmData.rader?.sidst_tjekket && (
                <div style={{ fontSize: 11, color: TEKST.dæmpet, marginTop: 4 }}>
                  Sidst tjekket: {new Date(llmData.rader.sidst_tjekket).toLocaleDateString('da-DK')}
                </div>
              )}
            </div>
          ) : llmData?.resultater?.length === 0 ? (
            <TomTilstand
              titel="Ingen LLM-tjek endnu"
              besked="Aktiver via POST /api/os/website-analytics/llm-visibility/run. Kræver OpenAI og/eller Perplexity API-nøgler i server .env."
              ikon="◈"
              badge="IKKE KONFIGURERET"
            />
          ) : (
            <TomTilstand
              titel="LLM-synlighed utilgængeligt"
              besked="Endpoint fejlede. Tjek at serveren er online."
              ikon="◈"
              badge="FEJL"
            />
          )}
        </Boks>

      </div>

      {/* ── Sideliste ────────────────────────────────────────────────────────── */}
      <Boks>
        <SektionsHeader
          label={`Live sider${!siderLoading && sider.length ? ` (${sider.length})` : ''}`}
          style={{ marginBottom: 14 }}
          højreIndhold={
            <button
              onClick={hentSider}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: TEKST.sekundær, fontSize: 12, padding: 0,
              }}
            >
              ↻ Genindlæs
            </button>
          }
        />

        {siderLoading ? (
          <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter sider fra GitHub…</div>
        ) : siderFejl ? (
          <TomTilstand
            titel="Kunne ikke hente live sider"
            besked={`Fejl: ${siderFejl}. Sitemap og GitHub fallback fejlede. Tjek at serveren kan nå skilsmissevejen.dk.`}
            ikon="◻"
            badge="FEJL"
          />
        ) : sider.length === 0 ? (
          <TomTilstand
            titel="Ingen live sider fundet"
            besked="Sitemappet på skilsmissevejen.dk returnerede 0 sider. Tjek at hjemmesiden er tilgængelig."
            ikon="◻"
            badge="IKKE FORBUNDET"
          />
        ) : (
          <DataTabel
            kolonner={siderKolonner}
            rækker={siderRækker}
            pageSize={20}
            tomTekst="Ingen sider"
          />
        )}
      </Boks>

      {/* ── AI-editor modal ───────────────────────────────────────────────────── */}
      {valgtSide && (
        <AIEditorModal
          side={valgtSide}
          onLuk={() => setValgtSide(null)}
        />
      )}

    </div>
  );
}
